import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://acbuyspreadsheet.one';

export default function ArticleSafety() {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Is AcBuy Spreadsheet Safe? Complete Safety & Buyer Protection Guide 2026",
    "description": "Everything you need to know about AcBuy Spreadsheet safety — community verification, seller vetting, scam prevention, and our honest assessment of trustworthiness.",
    "image": "https://readdy.ai/api/search-image?query=Clean%20minimalist%20trust%20and%20safety%20concept%20with%20shield%20and%20lock%20symbols%20on%20warm%20cream%20background%2C%20soft%20natural%20lighting%2C%20modern%20editorial%20style%2C%20protective%20calm%20atmosphere%2C%20professional%20photography&width=1200&height=500&seq=article-safety-hero-v3&orientation=landscape",
    "datePublished": "2026-06-20",
    "dateModified": "2026-06-25",
    "author": { "@type": "Organization", "name": "AcBuy Safety Team" },
    "publisher": { "@type": "Organization", "name": "AcBuy Spreadsheet", "url": siteUrl },
    "mainEntityOfPage": siteUrl + "/acbuy-spreadsheet-safe-guide"
  };
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main className="pt-20 pb-10">
        <article className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-foreground-800 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800">Is AcBuy Spreadsheet Safe?</span>
            </nav>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 leading-tight mb-4">
              Is AcBuy Spreadsheet Safe? Complete Safety &amp; Buyer Protection Guide 2026
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 flex-wrap">
              <span>Updated: June 2026</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>8 min read</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>By AcBuy Safety Team</span>
            </div>

            <div className="rounded-xl overflow-hidden mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20trust%20and%20safety%20concept%20with%20shield%20and%20lock%20symbols%20on%20warm%20cream%20background%2C%20soft%20natural%20lighting%2C%20modern%20editorial%20style%2C%20protective%20calm%20atmosphere%2C%20professional%20photography&width=1200&height=500&seq=article-safety-hero-v3&orientation=landscape"
                alt="AcBuy Spreadsheet safety guide"
                className="w-full h-56 md:h-72 object-cover object-top"
              />
            </div>

            <div className="bg-accent-50 border border-accent-200 rounded-xl p-5 md:p-6 mb-8">
              <p className="text-xs font-semibold text-accent-700 uppercase tracking-wider mb-2">Quick Answer</p>
              <p className="text-base md:text-lg font-semibold text-foreground-950 leading-relaxed">
                Yes, the <strong>AcBuy Spreadsheet</strong> is safe to use when you follow basic precautions. Every seller link is community-vetted, the spreadsheet contains no executable code, and you always complete purchases on the original platform — not through the spreadsheet itself.
              </p>
            </div>

            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">How AcBuy Spreadsheet Ensures Safety</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Safety is the number one priority for the <strong>AcBuy Spreadsheet</strong> community. Unlike random links you might find on social media or forums, every seller entry in the spreadsheet goes through a multi-step verification process before being included. This community-driven approach significantly reduces the risk of encountering fraudulent listings.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The verification process works like this: community members test seller links, verify product quality, check shipping reliability, and report their experiences. Only sellers that consistently receive positive feedback make it into the main spreadsheet. Suspicious or problematic sellers are flagged and removed, often within 24 hours of being reported.
              </p>
              <div className="bg-foreground-950 rounded-2xl p-6 md:p-8 mb-4">
                <h3 className="text-lg font-bold text-background-50 mb-4">Built-in Safety Features</h3>
                <ul className="space-y-3">
                  {[
                    'Community-vetted seller links with review history',
                    'Daily updates to remove outdated or broken links',
                    'Clear seller rating indicators within the spreadsheet',
                    'No executable code, tracking scripts, or downloads required',
                    'Purchases always completed on original seller platforms',
                    'Transparent feedback system with user reviews',
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-background-50/80">
                      <i className="ri-shield-check-line text-accent-400 mt-0.5 flex-shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">Personal Safety Precautions to Take</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                While the <strong>best AcBuy spreadsheet</strong> provides strong community protections, smart shoppers should always follow these additional safety steps to ensure a completely safe experience.
              </p>
              <div className="space-y-3 mb-4">
                {[
                  { title: 'Always Check QC Photos', desc: 'Quality control photos are your best defense. Request and carefully inspect QC photos before approving any order to ensure the product matches the listing description and images.' },
                  { title: 'Verify Seller Ratings', desc: 'Look at the seller rating and number of completed transactions in the spreadsheet. Higher ratings and more transactions generally indicate a more reliable seller with a proven track record.' },
                  { title: 'Use Secure Payment Methods', desc: 'Always use payment methods that offer buyer protection. Avoid direct bank transfers or payment methods that do not allow disputes or chargebacks if something goes wrong.' },
                  { title: 'Read Community Reviews', desc: 'The AcBuy community regularly posts reviews and feedback about sellers. Take a few minutes to read recent reviews before making a purchase, especially for higher-value items.' },
                ].map((tip, idx) => (
                  <div key={idx} className="flex gap-3 p-4 bg-background-100 rounded-xl">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <span className="text-xs font-bold text-accent-700">{idx + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-950 mb-1">{tip.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">Common Safety Myths Debunked</h2>
              <div className="space-y-3">
                {[
                  { myth: 'The spreadsheet can track your activity', truth: 'False. The spreadsheet is a static document with no tracking capabilities. It does not run scripts or collect any personal data from users.' },
                  { myth: 'All sellers in the spreadsheet are risky', truth: 'False. The spreadsheet exclusively features community-vetted sellers. While no system is perfect, the vetting process significantly reduces risk compared to unverified browsing.' },
                  { myth: 'You need special software to use it safely', truth: 'False. The AcBuy Spreadsheet works in any standard web browser or spreadsheet application. No downloads, plugins, or special software are required.' },
                ].map((item, idx) => (
                  <div key={idx} className="bg-background-100 rounded-xl p-5">
                    <p className="text-sm font-semibold text-secondary-700 mb-1">Myth: {item.myth}</p>
                    <p className="text-sm text-foreground-600">{item.truth}</p>
                  </div>
                ))}
              </div>
            </section>

            <div className="bg-primary-50 border border-primary-200 rounded-2xl p-6 md:p-8 text-center mb-8">
              <h3 className="text-lg font-bold text-foreground-950 mb-3">Ready to Shop Safely?</h3>
              <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto leading-relaxed">
                Access the verified AcBuy Spreadsheet and start shopping with confidence today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold px-8 py-3.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer">
                  Browse Spreadsheet <i className="ri-arrow-right-line"></i>
                </a>
                <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-arrow-left-line"></i> Back to Home
                </Link>
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-8">
              <h3 className="text-sm font-bold text-foreground-950 mb-4">Explore More</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="text-sm text-foreground-600 hover:text-primary-600 px-4 py-2 bg-background-100 rounded-full transition-colors whitespace-nowrap">Homepage</Link>
                <Link to="/acbuy-spreadsheet-guide" className="text-sm text-foreground-600 hover:text-primary-600 px-4 py-2 bg-background-100 rounded-full transition-colors whitespace-nowrap">Buying Guide</Link>
                <Link to="/acbuy-spreadsheet-buying-tips" className="text-sm text-foreground-600 hover:text-primary-600 px-4 py-2 bg-background-100 rounded-full transition-colors whitespace-nowrap">Buying Tips</Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}